/**
 * Tab-scoped Kanban board sort persistence (sessionStorage).
 *
 * Product rules:
 * - Survives reload / in-tab navigation (sessionStorage).
 * - Cleared when the tab/browser session ends.
 * - Keyed by Auth0 `user.sub` + `jobId` so profiles never share preferences.
 * - No independent wall-clock TTL; validity follows Auth0 identity availability
 *   (no persist / no restore while `user.sub` is missing).
 * - Does not migrate or read legacy localStorage keys.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

function storageKey(userKey: string, jobId: string): string {
  return `kanban-sort-by:${userKey}:${jobId}`;
}

function isAllowedSortBy(
  sortBy: string,
  allowedIds?: readonly string[]
): boolean {
  if (!allowedIds || allowedIds.length === 0) return true;
  return allowedIds.includes(sortBy);
}

function normalizeStoredSortBy(stored: string): string | null {
  const trimmed = stored.trim();
  if (!trimmed) return null;

  // Accept plain ids; also tolerate accidental JSON-string encoding.
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      return typeof parsed === 'string' && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  // Reject object/array payloads (e.g. abandoned TTL wrappers).
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return null;
  }

  return trimmed;
}

function readRaw(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string | null): void {
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // ignore quota / private-mode failures
  }
}

/** Drop leftover job-only localStorage keys from the first persistence commit. */
function purgeLegacyLocalStorage(jobId: string): void {
  if (!jobId) return;
  try {
    localStorage.removeItem(`kanban-sort-by-${jobId}`);
  } catch {
    // ignore
  }
}

export function readKanbanSortBy(
  userKey: string | null | undefined,
  jobId: string,
  allowedIds?: readonly string[]
): string | null {
  if (!userKey || !jobId) return null;

  purgeLegacyLocalStorage(jobId);

  const key = storageKey(userKey, jobId);
  const stored = readRaw(key);
  if (stored == null) return null;

  const normalized = normalizeStoredSortBy(stored);
  if (normalized == null || !isAllowedSortBy(normalized, allowedIds)) {
    writeRaw(key, null);
    return null;
  }

  return normalized;
}

export function writeKanbanSortBy(
  userKey: string | null | undefined,
  jobId: string,
  sortBy: string | null
): void {
  if (!userKey || !jobId) return;

  const key = storageKey(userKey, jobId);
  if (sortBy == null || sortBy === '') {
    writeRaw(key, null);
    return;
  }
  writeRaw(key, sortBy);
}

export function useKanbanSortBy(
  jobId: string,
  allowedIds?: readonly string[]
) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const userKey =
    isAuthenticated && !isAuthLoading && typeof user?.sub === 'string' && user.sub
      ? user.sub
      : null;

  const [sortBy, setSortByState] = useState<string | null>(() =>
    readKanbanSortBy(userKey, jobId, allowedIds)
  );

  // Sort chosen while Auth0 identity was unavailable — flush once `userKey` exists.
  const pendingSortRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!userKey) {
      setSortByState(null);
      return;
    }

    if (pendingSortRef.current !== undefined) {
      const pending = pendingSortRef.current;
      pendingSortRef.current = undefined;
      const normalized =
        pending == null ||
        pending === '' ||
        !isAllowedSortBy(pending, allowedIds)
          ? null
          : pending;
      setSortByState(normalized);
      writeKanbanSortBy(userKey, jobId, normalized);
      return;
    }

    setSortByState(readKanbanSortBy(userKey, jobId, allowedIds));
  }, [userKey, jobId, allowedIds]);

  const setSortBy = useCallback(
    (next: string | null) => {
      const normalized =
        next == null || next === '' || !isAllowedSortBy(next, allowedIds)
          ? null
          : next;

      setSortByState(normalized);

      if (!userKey) {
        pendingSortRef.current = normalized;
        return;
      }

      pendingSortRef.current = undefined;
      writeKanbanSortBy(userKey, jobId, normalized);
    },
    [userKey, jobId, allowedIds]
  );

  return { sortBy, setSortBy };
}
